import { useEffect, useState } from "react";
import { CheckCircle2, Clock, Zap, ShoppingBag } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { resolveFileUrl } from "@/components/FileDropzone";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function StatusBadge({ order }) {
  const label = order.sold
    ? "Completed"
    : order.paid_by_buyer
    ? "Awaiting seller"
    : "Awaiting payment";
  const color = order.sold
    ? "bg-[#F7931A]"
    : order.paid_by_buyer
    ? "bg-yellow-200"
    : "bg-white";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 border-2 border-black text-[10px] font-bold uppercase tracking-widest ${color}`}>
      {order.sold ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
      {label}
    </span>
  );
}

function OrderRow({ order, side, onConfirm }) {
  const listing = order.listing;
  const other = side === "buying" ? order.seller : order.buyer;
  return (
    <div
      data-testid={`order-${order.id}`}
      className="border-2 border-black bg-white p-4 flex items-center gap-4"
    >
      <div className="w-16 h-16 border-2 border-black overflow-hidden bg-[#F7931A]/10 shrink-0">
        {listing?.image_url ? (
          <img src={resolveFileUrl(listing.image_url)} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-display font-black text-2xl text-[#F7931A]">₿</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold truncate">{listing?.title || "Listing"}</div>
        <div className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60">
          {side === "buying" ? "Seller" : "Buyer"} · {other?.name || "—"}
        </div>
        <div className="font-mono text-sm mt-1 text-[#F7931A] font-bold">
          {order.btc_price} BTC ({order.amount_sats} sats)
        </div>
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0">
        <StatusBadge order={order} />
        {side === "selling" && order.paid_by_buyer && !order.sold && (
          <button
            onClick={() => onConfirm(order)}
            data-testid={`btn-confirm-${order.id}`}
            className="px-3 py-2 bg-[#F7931A] border-2 border-black text-xs font-bold uppercase tracking-widest btn-brutal"
          >
            Confirm received
          </button>
        )}
      </div>
    </div>
  );
}

export default function Orders() {
  const { user } = useAuth();
  const [data, setData] = useState({ buying: [], selling: [] });
  const [tab, setTab] = useState("selling");

  const load = async () => {
    const r = await api.get("/orders/my");
    setData(r.data);
  };

  useEffect(() => {
    load();
  }, []);

  const confirm = async (order) => {
    try {
      await api.post(`/orders/${order.id}/confirm-received`);
      toast.success(`Confirmed. Your 2% platform fee (${order.platform_fee_btc} BTC) is tracked.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const totalPendingFee = data.selling
    .filter((o) => o.sold)
    .reduce((s, o) => s + (o.platform_fee_btc || 0), 0);

  return (
    <main className="max-w-4xl mx-auto px-4 py-10">
      <div className="flex items-center gap-3 mb-2">
        <ShoppingBag className="w-6 h-6 text-[#F7931A]" />
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Orders</h1>
      </div>
      <p className="text-sm text-[#0A0A0A]/60 font-mono uppercase mb-6">
        Lightning checkouts · signed in as {user?.name}
      </p>

      <div className="border-2 border-black bg-white flex mb-6" data-testid="orders-tabs">
        <button
          onClick={() => setTab("selling")}
          data-testid="tab-selling"
          className={`flex-1 py-3 font-bold uppercase tracking-widest text-sm border-r-2 border-black ${
            tab === "selling" ? "bg-[#F7931A]" : "bg-white hover:bg-[#F7931A]/10"
          }`}
        >
          Selling ({data.selling.length})
        </button>
        <button
          onClick={() => setTab("buying")}
          data-testid="tab-buying"
          className={`flex-1 py-3 font-bold uppercase tracking-widest text-sm ${
            tab === "buying" ? "bg-[#F7931A]" : "bg-white hover:bg-[#F7931A]/10"
          }`}
        >
          Buying ({data.buying.length})
        </button>
      </div>

      {tab === "selling" && totalPendingFee > 0 && (
        <div className="mb-6 border-2 border-black bg-[#0A0A0A] text-[#FDFBF7] p-4 flex items-center justify-between">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-[#F7931A] flex items-center gap-2">
              <Zap className="w-3 h-3" /> Platform fee tracker
            </div>
            <div className="font-display font-black text-2xl mt-1">
              {totalPendingFee.toFixed(8)} BTC owed
            </div>
          </div>
          <div className="text-xs text-white/70 font-mono uppercase tracking-widest text-right">
            2% of all completed sales
          </div>
        </div>
      )}

      <div className="space-y-3">
        {(data[tab] || []).length === 0 ? (
          <div className="border-2 border-black bg-white p-12 text-center font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60">
            No {tab} orders yet.
          </div>
        ) : (
          data[tab].map((o) => (
            <OrderRow key={o.id} order={o} side={tab} onConfirm={confirm} />
          ))
        )}
      </div>
    </main>
  );
}
